import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useMutation } from "@tanstack/react-query";
import { Eye, EyeOff, Lock, Mail, Phone, Star, User } from "lucide-react";
import { extractPreAuthToken, teacherApi } from "../../features/teacher/teacherApi";
import { setTeacherSignupContext } from "../../features/teacher/teacherSlice";
import {
  TeacherAuthField,
  TeacherAuthLayout,
  TeacherFeedbackMessage,
} from "./TeacherUI";
import {
  normalizeTeacherPhoneNumber,
} from "../../utils/teacherPhone";
import {
  TEACHER_YEAR_OPTIONS,
  filterSupportedTeacherYearIds,
} from "../../utils/teacherYears";
import { getTeacherAuthErrorMessage } from "../../utils/uiErrorMessages";

const initialFormData = {
  name: "",
  email: "",
  phone: "",
  password: "",
  year: [],
  experience: "",
};

const SIGNUP_FIELD_ERROR_ALIASES = {
  name: ["name", "full_name", "fullName"],
  email: ["email", "email_address"],
  phone: ["phone", "phone_number", "mobile", "mobile_number"],
  password: ["password"],
  year: ["year", "years", "year_id", "year_ids"],
  experience: ["experience"],
};

const normalizeSignupFieldErrors = (fieldErrors = {}) =>
  Object.entries(SIGNUP_FIELD_ERROR_ALIASES).reduce(
    (result, [fieldName, aliases]) => {
      const matchedAlias = aliases.find((alias) =>
        String(fieldErrors?.[alias] || "").trim()
      );

      if (matchedAlias) {
        result[fieldName] = String(fieldErrors[matchedAlias] || "").trim();
      }

      return result;
    },
    {}
  );

const countPhoneDigits = (value = "") =>
  String(value || "").replace(/\D/g, "").length;

export default function TeacherSignup() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [formData, setFormData] = useState(initialFormData);
  const [fieldErrors, setFieldErrors] = useState({});
  const [generalMessage, setGeneralMessage] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const signupMutation = useMutation({
    mutationFn: teacherApi.signup,
    onSuccess: (payload) => {
      const preAuthToken = extractPreAuthToken(payload);

      if (!preAuthToken) {
        setGeneralMessage(
          "Signup succeeded but the backend did not return a pre_auth_token."
        );
        return;
      }

      dispatch(
        setTeacherSignupContext({
          preAuthToken,
          email: formData.email.trim(),
          source: "signup",
        })
      );

      navigate("/teacherverify-otp", {
        replace: true,
        state: {
          preAuthToken,
          email: formData.email.trim(),
          source: "signup",
        },
      });
    },
    onError: (error) => {
      const nextFieldErrors = normalizeSignupFieldErrors(error.fieldErrors || {});
      const hasFieldErrors = Object.keys(nextFieldErrors).length > 0;

      setFieldErrors(nextFieldErrors);
      setGeneralMessage(
        hasFieldErrors ? "" : getTeacherAuthErrorMessage(error, "signup")
      );
    },
  });

  const handleChange = (event) => {
    const { name, value } = event.target;
    const nextValue =
      name === "experience"
        ? value === "" || /^\d+$/.test(value)
          ? value
          : formData.experience
        : value;

    setFormData((previous) => ({
      ...previous,
      [name]: nextValue,
    }));

    if (fieldErrors[name]) {
      setFieldErrors((previous) => ({
        ...previous,
        [name]: "",
      }));
    }

    if (generalMessage) {
      setGeneralMessage("");
    }
  };

  const handleYearToggle = (yearId) => {
    setFormData((previous) => {
      const selectedYears = new Set(filterSupportedTeacherYearIds(previous.year));

      if (selectedYears.has(yearId)) {
        selectedYears.delete(yearId);
      } else {
        selectedYears.add(yearId);
      }

      return {
        ...previous,
        year: TEACHER_YEAR_OPTIONS.map((option) => option.id).filter((optionId) =>
          selectedYears.has(optionId)
        ),
      };
    });

    if (fieldErrors.year) {
      setFieldErrors((previous) => ({
        ...previous,
        year: "",
      }));
    }

    if (generalMessage) {
      setGeneralMessage("");
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    setFieldErrors({});
    setGeneralMessage("");

    const nextErrors = {};
    ["name", "email", "phone", "password"].forEach((fieldName) => {
      if (!String(formData[fieldName] || "").trim()) {
        nextErrors[fieldName] = `${fieldName.charAt(0).toUpperCase()}${fieldName.slice(
          1
        )} is required.`;
      }
    });

    const normalizedYears = filterSupportedTeacherYearIds(formData.year);

    if (!normalizedYears.length) {
      nextErrors.year = "Select at least one year.";
    }

    if (Object.keys(nextErrors).length) {
      setFieldErrors(nextErrors);
      return;
    }

    if (formData.password.length < 8) {
      setFieldErrors({
        password: "Password must be at least 8 characters long.",
      });
      return;
    }

    const normalizedExperience = formData.experience.trim();
    const normalizedPhone =
      normalizeTeacherPhoneNumber(formData.phone) || formData.phone.trim();

    if (countPhoneDigits(normalizedPhone) < 5) {
      setFieldErrors({
        phone: "Please enter a valid number",
      });
      return;
    }

    if (
      normalizedExperience &&
      (!/^\d+$/.test(normalizedExperience) || Number(normalizedExperience) < 0)
    ) {
      setFieldErrors({
        experience: "Please enter 0 or more whole years.",
      });
      return;
    }

    signupMutation.mutate({
      name: formData.name.trim(),
      email: formData.email.trim(),
      phone: normalizedPhone,
      password: formData.password,
      year: normalizedYears,
      ...(normalizedExperience
        ? { experience: Number(normalizedExperience) }
        : {}),
    });
  };

  const handlePhoneBlur = () => {
    const normalizedPhone =
      normalizeTeacherPhoneNumber(formData.phone) || formData.phone.trim();
    if (!normalizedPhone || normalizedPhone === formData.phone) {
      return;
    }

    setFormData((previous) => ({
      ...previous,
      phone: normalizedPhone,
    }));

    if (fieldErrors.phone) {
      setFieldErrors((previous) => ({
        ...previous,
        phone: "",
      }));
    }
  };

  return (
    <TeacherAuthLayout
      title="Teacher Signup"
      subtitle="Create your account and continue to OTP verification."
      cardClassName="teacher-auth-card-compact"
      wide
      closeTo="/teacherlogin"
      footer={
        <p className="teacher-auth-footer-text">
          Already registered?{" "}
          <Link className="teacher-auth-link" to="/teacherlogin">
            Back to login
          </Link>
        </p>
      }
    >
      {generalMessage ? (
        <TeacherFeedbackMessage variant="error">{generalMessage}</TeacherFeedbackMessage>
      ) : null}

      <form className="teacher-auth-form" onSubmit={handleSubmit} noValidate>
        <div className="teacher-auth-grid">
          <TeacherAuthField
            id="teacher-signup-name"
            label="Name"
            icon={User}
            name="name"
            required
            type="text"
            value={formData.name}
            onChange={handleChange}
            placeholder="Enter your full name"
            error={fieldErrors.name}
          />

          <TeacherAuthField
            id="teacher-signup-email"
            label="Email"
            icon={Mail}
            name="email"
            required
            type="email"
            value={formData.email}
            onChange={handleChange}
            placeholder="Enter your email"
            error={fieldErrors.email}
          />

          <TeacherAuthField
            id="teacher-signup-phone"
            label="Phone"
            icon={Phone}
            name="phone"
            required
            type="tel"
            value={formData.phone}
            onChange={handleChange}
            onBlur={handlePhoneBlur}
            placeholder="Enter your phone number"
            error={fieldErrors.phone}
          />

          <TeacherAuthField
            id="teacher-signup-password"
            label="Password"
            icon={Lock}
            name="password"
            required
            type={showPassword ? "text" : "password"}
            value={formData.password}
            onChange={handleChange}
            placeholder="Create a password"
            error={fieldErrors.password}
            note="Password must be at least 8 characters."
            trailingAction={
              <button
                className="teacher-auth-toggle"
                type="button"
                onClick={() => setShowPassword((previous) => !previous)}
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            }
          />

          <div className="teacher-auth-field teacher-auth-grid-full">
            <div className="teacher-auth-label" id="teacher-signup-year-label">
              Years You Teach
              <span className="teacher-auth-label-required" aria-hidden="true">
                *
              </span>
            </div>
            <div
              role="group"
              aria-labelledby="teacher-signup-year-label"
              className="teacher-auth-chip-grid"
            >
              {TEACHER_YEAR_OPTIONS.map((yearOption) => {
                const isSelected = formData.year.includes(yearOption.id);

                return (
                  <button
                    key={yearOption.id}
                    type="button"
                    className={`teacher-auth-chip${
                      isSelected ? " teacher-auth-chip-active" : ""
                    }`}
                    aria-pressed={isSelected}
                    onClick={() => handleYearToggle(yearOption.id)}
                  >
                    {yearOption.label}
                  </button>
                );
              })}
            </div>
            <p className="teacher-auth-note">
              Select one or more years from Foundation to Year 9.
            </p>
            {fieldErrors.year ? (
              <p className="teacher-auth-field-error">{fieldErrors.year}</p>
            ) : null}
          </div>

          <TeacherAuthField
            id="teacher-signup-experience"
            label="Years of Experience"
            icon={Star}
            name="experience"
            type="number"
            min="0"
            step="1"
            inputMode="numeric"
            value={formData.experience}
            onChange={handleChange}
            placeholder="Enter years of experience"
            error={fieldErrors.experience}
            onKeyDown={(event) => {
              if (["-", "+", ".", "e", "E"].includes(event.key)) {
                event.preventDefault();
              }
            }}
          />

        </div>

        <button className="teacher-auth-button" type="submit" disabled={signupMutation.isPending}>
          {signupMutation.isPending ? "Creating account..." : "Continue to OTP"}
        </button>
      </form>
    </TeacherAuthLayout>
  );
}
